//180226
//with preview of the keyboard input in the chat box, and the keyboard will be hidden after pressing enter. The blackboard can be clicked to show the keyboard. The code is integrated with your custom keyboard component and should work with your existing setup.
import { Component, Property, Type } from "@wonderlandengine/api";
import {
  addStablePointerSelect,
  ensureInteractable,
} from "./interactable.js";
import {
  DEFAULT_TEXTURE_PROPERTY,
  createStreamTextureOnObject,
  createVideoTextureOnObject,
} from "./video-texture-board.js";

// 全局变量
let Peer = null;
let isHost = false;

const REMOTE_MOVING_SPEED_THRESHOLD = 0.05;
const REMOTE_NAME_TAG_OFFSET_Y = 0.45;
const REMOTE_HEAD_MARKER_SCALE = 0.045;
const REMOTE_NAME_TAG_SCALE = 0.75;
const DEFAULT_NETWORK_SEND_INTERVAL_S = 0.05;
const REMOTE_TRANSFORM_SMOOTHING = 14;
const CHAT_BOARD_HITBOX_EXTENTS = [0.12, 0.8, 0.8];
const CHAT_BOARD_MAX_CHARACTERS_PER_LINE = 38;
const CHAT_BOARD_MAX_RENDERED_LINES = 7;
const CHAT_BOARD_CONTINUATION_INDENT = "  ";
const PLAYER_TRANSFORM_MESSAGE_TYPE = "player-transform";
const EXPERIMENT_DATA_MESSAGE_TYPE = "experiment-data";

export class PeerManager extends Component {
  static TypeName = "peer-manager";
  static Properties = {
    serverId: Property.string("room-01"),
    networkSendFrequencyInS: Property.float(DEFAULT_NETWORK_SEND_INTERVAL_S),
    playerHead: Property.object(),
    playerDesktopHead: Property.object(),
    playerRightHand: Property.object(),
    playerLeftHand: Property.object(),
    networkPlayerPool: Property.object(),
    voiceEnabled: Property.bool(true),
    blackboardText: Property.object(),
    chatBlackboardText: Property.object(),
    tutorialBlackboardText: Property.object(),
    tutorialVideoBoardObject: Property.object(),
    tutorialVideoUrl: Property.string("video.mp4"),
    hostVideoBoardObject: Property.object(),
    tutorialText: Property.string(
      "Tutorial\n1. Host starts a room.\n2. Join connects to the room id.\n3. Click the left board to type.\n4. Press Enter to send.",
    ),

    // 👇 键盘物体绑定
    vrKeyboardObject: Property.object(),
    // 👇【新增】键盘预览框绑定 (拖入刚才建的 PreviewScreen)
    keyboardPreviewText: Property.object(),
  };

  init() {
    this.streams = {};
    this.activePlayers = {};
    this.currentDataPacket = {};
    this.calls = {};
    this.connections = [];
    this.currentTime = 0.0;
    this.chatHistory = [];
    this.maxChatLines = 6;
    this.maxChatRenderedLines = CHAT_BOARD_MAX_RENDERED_LINES;
    this.fallbackUsername = "User_" + Math.floor(Math.random() * 1000);
    const loginSettings = this._loadLoginSettings();
    this.myUsername = loginSettings.username || this.fallbackUsername;
    this.playerMetadata = {};
    this.serverId = loginSettings.roomId || this.serverId;
    this.preferredRole = loginSettings.role || "guest";
    this.role = "idle";
    this.isConnecting = false;
    this.isMuted = false;
    this.localStream = null;
    this.localAudioPromise = null;
    this.hostMediaPromise = null;

    this.headDualQuat = new Float32Array(8);
    this.rightHandDualQuat = new Float32Array(8);
    this.leftHandDualQuat = new Float32Array(8);
    this.currentHeadPosition = new Float32Array(3);
    this.previousHeadPosition = new Float32Array(3);
    this.hasPreviousHeadPosition = false;
    this.localMovementState = { state: "idle", moving: false, speed: 0 };
    this.fallbackDesktopHead = null;

    this.audio = document.createElement("AUDIO");
    this.audio.id = "localAudio";
    document.body.appendChild(this.audio);

    import("peerjs").then((module) => {
      Peer = module.Peer;
      console.log("✅ PeerJS Loaded");
    });

    this.connectionEstablishedCallbacks = [];
    this.clientJoinedCallbacks = [];
    this.disconnectCallbacks = [];
    this.registeredNetworkCallbacks = {};
    this.remoteAudioElements = {};
    this.isDisconnecting = false;
    this.videoBoards = [];
    this.tutorialVideo = null;
    this.hostCameraPreview = null;
    this.remoteHostVideo = null;

    this._createChatUI();
    this._createLoginUI();
  }

  _setRole(role) {
    this.role = role;
    isHost = role === "host";
  }

  start() {
    this.chatBoardText = this.chatBlackboardText || this.blackboardText;
    this.tutorialBoardText = this.tutorialBlackboardText;
    this._updateTutorialBoard();
    this._setupVRInput();
    this._setupCustomKeyboard();
    this._setupTutorialVideo();

    if (this.voiceEnabled && !navigator.mediaDevices?.getUserMedia) {
      this._appendChatToBoard(
        "System",
        "Microphone is not available in this browser.",
      );
    }

    if (this.networkPlayerPool) {
      this.networkPlayerSpawner =
        this.networkPlayerPool.getComponent("peer-networked-player-pool") ||
        this.networkPlayerPool.getComponent("peer-networked-player-spawner");
    }
    window.peerManager = this;
  }

  _setLocalStream(stream) {
    if (this.localStream && this.localStream !== stream) {
      this.localStream.getTracks().forEach((track) => track.stop());
    }
    this.localStream = stream;
    this._applyMuteState();
    return stream;
  }

  _ensureGuestMicrophone() {
    if (!this.voiceEnabled) return Promise.resolve(null);
    if (this.localStream?.getAudioTracks().length) {
      return Promise.resolve(this.localStream);
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      this._appendChatToBoard(
        "System",
        "Microphone is not available in this browser.",
      );
      return Promise.resolve(null);
    }
    if (this.localAudioPromise) return this.localAudioPromise;

    this.localAudioPromise = navigator.mediaDevices
      .getUserMedia({ audio: true, video: false })
      .then((stream) => this._setLocalStream(stream))
      .catch((err) => {
        this.localStream = null;
        this._appendChatToBoard(
          "System",
          "Microphone permission was denied. Voice chat is disabled.",
        );
        console.error("Mic denied", err);
        return null;
      })
      .finally(() => {
        this.localAudioPromise = null;
      });

    return this.localAudioPromise;
  }

  _ensureLocalMediaForCurrentRole() {
    if (this.role === "host" || this.peer?.id === this.serverId) {
      return this._startHostCameraPreview();
    }
    return this._ensureGuestMicrophone();
  }

  _handleIncomingMediaCall(call) {
    if (!this.voiceEnabled) return;
    this.calls[call.peer] = call;
    this._ensureLocalMediaForCurrentRole().then((stream) => {
      if (!stream) {
        this._appendChatToBoard(
          "System",
          "Cannot answer voice call without microphone access.",
        );
        return;
      }
      call.answer(stream);
      call.on("stream", (s) => this._addAudioStream(call.peer, s));
    });
  }

  _callWhenLocalMediaReady(id) {
    if (!this.voiceEnabled || !this.peer || !id) return;
    this._ensureLocalMediaForCurrentRole().then((stream) => {
      if (stream) this.call(id);
    });
  }

  _setVideoBoard(slot, board) {
    const previous = this[slot];
    if (previous && previous !== board) previous.stop?.();

    this.videoBoards = this.videoBoards.filter(
      (item) => item && item !== previous && item !== board,
    );
    this[slot] = board;

    if (board) this.videoBoards.push(board);
    return board;
  }

  _createHostVideoBoard(stream) {
    const board = createStreamTextureOnObject(
      this.engine,
      this.hostVideoBoardObject || "videoboard",
      stream,
      { textureProperty: DEFAULT_TEXTURE_PROPERTY, muted: true },
    );

    if (board) this._setVideoBoard("hostCameraPreview", board);
    return board;
  }

  _setupTutorialVideo() {
    const tutorialVideo = createVideoTextureOnObject(
      this.engine,
      this.tutorialVideoBoardObject || "tutorialboard",
      {
        url: this.tutorialVideoUrl || "video.mp4",
        textureProperty: DEFAULT_TEXTURE_PROPERTY,
        autoplay: true,
        loop: true,
        muted: true,
      },
    );
    if (tutorialVideo) this._setVideoBoard("tutorialVideo", tutorialVideo);
  }

  toggleTutorialVideo() {
    if (!this.tutorialVideo) {
      this._appendChatToBoard("System", "Tutorial video is not ready.");
      return false;
    }
    return this.tutorialVideo.togglePlay();
  }

  replayTutorialVideo() {
    if (!this.tutorialVideo) {
      this._appendChatToBoard("System", "Tutorial video is not ready.");
      return;
    }
    this.tutorialVideo.replay();
  }

  // --- 🎹 核心：德语键盘与预览框集成 ---
  _setupCustomKeyboard() {
    if (!this.vrKeyboardObject) return;

    this.keyboardComp = this.vrKeyboardObject.getComponent("keyboard");

    if (this.keyboardComp) {
      // 监听按键变化
      this.keyboardComp.addTextChangedCallback((currentText) => {
        // 1. 实时预览逻辑：把正在输入的字显示在预览框里
        if (this.keyboardPreviewText) {
          const textComp = this.keyboardPreviewText.getComponent("text");
          if (textComp) {
            // 加上一个 "|" 模拟光标效果
            textComp.text = currentText + "|";
          }
        }

        // 2. 发送逻辑：检测回车
      });
      this.keyboardComp.addSubmitCallback((submittedText) => {
        this._submitChatText(submittedText);
      });
    }
  }

  _showKeyboard() {
    if (!this.vrKeyboardObject) return;
    this.vrKeyboardObject.active = true;
    if (this.keyboardComp) {
      this.keyboardComp.active = true;
      this.keyboardComp.setText("");
      this.keyboardComp.show();
    }
    if (this.keyboardPreviewText) {
      const textComp = this.keyboardPreviewText.getComponent("text");
      if (textComp) textComp.text = "";
    }
  }

  _hideKeyboard() {
    if (this.keyboardComp) this.keyboardComp.text = "";
    if (this.keyboardPreviewText) {
      const textComp = this.keyboardPreviewText.getComponent("text");
      if (textComp) textComp.text = "";
    }
    if (this.vrKeyboardObject) this.vrKeyboardObject.active = false;
  }

  _setupVRInput() {
    if (this.chatBoardText) {
      const { target } = ensureInteractable(this.chatBoardText, {
        extents: CHAT_BOARD_HITBOX_EXTENTS,
      });

      addStablePointerSelect(target, () => this._showKeyboard());
    }
  }

  _submitChatText(text) {
    const message = `${text || ""}`.trim();
    if (!message) {
      this._hideKeyboard();
      return false;
    }

    this.sendChat(message);
    this._hideKeyboard();
    return true;
  }

  sendChat(text) {
    const message = `${text || ""}`.trim();
    if (!message) return null;

    const data = this._createChatMessage(message);
    this._appendChatMessage(data);
    if (isHost) this._sendToGuests(data);
    else this._sendToHost(data);
    return data;
  }

  _createChatMessage(text) {
    const senderName = this._getDisplayName();
    return {
      type: "chat",
      text,
      msg: text,
      sender: senderName,
      senderId: this.peer ? this.peer.id : "unknown",
      id: this.peer ? this.peer.id : "unknown",
      roomId: this.serverId,
      createdAt: Date.now(),
    };
  }

  _normalizeChatMessage(data, fallbackSender = "Unknown") {
    const text = `${data?.text ?? data?.msg ?? ""}`.trim();
    if (!text) return null;

    const sender = data?.sender || fallbackSender;
    return {
      type: "chat",
      text,
      msg: text,
      sender,
      senderId: data?.senderId || data?.id || "unknown",
      id: data?.id || data?.senderId || "unknown",
      roomId: data?.roomId || this.serverId,
      createdAt: data?.createdAt || Date.now(),
    };
  }

  _appendChatMessage(data, fallbackSender = "Unknown") {
    const message = this._normalizeChatMessage(data, fallbackSender);
    if (!message) return null;

    this._appendChatToBoard(message.sender, message.text);
    return message;
  }

  sendData(data, options = {}) {
    const message = this._createDataMessage(data);
    if (!message) return null;

    const canSendAsHost = this.role === "host" || isHost;
    const canSendAsGuest = this.connection?.open;
    if (!canSendAsHost && !canSendAsGuest) {
      this._appendChatToBoard(
        "System",
        "Data message was not sent because networking is not connected.",
      );
      return null;
    }

    if (options.localEcho ?? true) this._dispatchNetworkData(message);
    if (canSendAsHost) this._sendToGuests(message, options.exceptPeerId || null);
    else this._sendToHost(message);
    return message;
  }

  sendExperimentData(experimentOrData, key, value, extra = {}) {
    const data =
      typeof experimentOrData === "object" && experimentOrData !== null
        ? experimentOrData
        : {
            experimentId: experimentOrData,
            key,
            value,
            ...extra,
          };

    return this.sendData({
      ...data,
      type: EXPERIMENT_DATA_MESSAGE_TYPE,
    });
  }

  sendExperimentParameter(experimentId, key, value, extra = {}) {
    return this.sendExperimentData(experimentId, key, value, extra);
  }

  _createDataMessage(data) {
    if (!data || typeof data !== "object") return null;

    const senderId = this.peer?.id || this.serverId || "unknown";
    return {
      ...data,
      type: data.type || "data",
      sender: data.sender || this._getDisplayName(),
      senderId: data.senderId || senderId,
      id: data.id || senderId,
      roomId: data.roomId || this.serverId,
      createdAt: data.createdAt || Date.now(),
    };
  }

  _isCustomDataMessage(data) {
    return Boolean(
      data?.type &&
        data.type !== "chat" &&
        data.type !== "peer-list" &&
        data.type !== PLAYER_TRANSFORM_MESSAGE_TYPE,
    );
  }

  _handleCustomDataMessage(data, sourcePeerId = null, relayFromHost = false) {
    if (!this._isCustomDataMessage(data)) return false;

    const message = this._createDataMessage({
      ...data,
      senderId: data.senderId || data.id || sourcePeerId || "unknown",
      id: data.id || data.senderId || sourcePeerId || "unknown",
    });
    if (!message) return false;

    this._dispatchNetworkData(message);
    if (relayFromHost) this._sendToGuests(message, sourcePeerId);
    return true;
  }

  _dispatchNetworkData(data) {
    const callbacks = [
      this.registeredNetworkCallbacks[data.type],
      this.registeredNetworkCallbacks["*"],
    ].filter((callback) => typeof callback === "function");

    for (const callback of callbacks) {
      try {
        callback(data);
      } catch (err) {
        console.error("Network data callback failed", err);
      }
    }
  }

  _sendToGuests(data, exceptPeerId = null) {
    for (const conn of this.connections) {
      if (conn.peer !== exceptPeerId && conn.open) conn.send(data);
    }
  }

  _sendToHost(data) {
    if (this.connection?.open) this.connection.send(data);
  }

  _getDisplayName() {
    const name = this.myUsername || this.fallbackUsername;
    if (this.role === "host") return `Host: ${name}`;
    return name;
  }

  _loadLoginSettings() {
    try {
      return JSON.parse(localStorage.getItem("communication-login")) || {};
    } catch {
      return {};
    }
  }

  _saveLoginSettings(username, roomId, role) {
    const settings = { username, roomId, role };
    localStorage.setItem("communication-login", JSON.stringify(settings));
    window.communicationLogin = settings;
  }

  _appendLoginStatus() {
    const action =
      this.preferredRole === "host"
        ? "Bitte klicke Host, um den Raum zu erstellen."
        : "Bitte klicke Join, nachdem der Host den Raum erstellt hat.";
    this._appendChatToBoard(
      "System",
      `Angemeldet als ${this.myUsername}, Raum: ${this.serverId}, Rolle: ${this.preferredRole}. ${action}`,
    );
  }

  _showPeerError(err, mode) {
    const type = err?.type || "";
    let message = `Verbindungsfehler: ${type || err?.message || "unknown"}`;

    if (mode === "host" && type === "unavailable-id") {
      message = `Raum ${this.serverId} ist bereits belegt. Bitte eine andere Room ID verwenden oder dem bestehenden Raum als Gast beitreten.`;
    } else if (
      mode === "guest" &&
      (type === "peer-unavailable" || type === "network")
    ) {
      message = `Raum ${this.connectionId || this.serverId} nicht gefunden. Bitte pruefen, ob der Host diesen Raum bereits gestartet hat.`;
    }

    this._appendChatToBoard("System", message);
  }

  _appendChatToBoard(sender, message) {
    const line = `[${sender}]: ${message}`;
    this.chatHistory.push(line);
    if (this.chatHistory.length > this.maxChatLines) this.chatHistory.shift();
    if (this.chatBoardText) {
      const textComp = this.chatBoardText.getComponent("text");
      if (textComp) textComp.text = this._formatChatBoardText();
    }
  }

  _formatChatBoardText() {
    const renderedLines = [];
    for (const line of this.chatHistory) {
      renderedLines.push(...this._wrapChatBoardLine(line));
    }
    return renderedLines.slice(-this.maxChatRenderedLines).join("\n");
  }

  _wrapChatBoardLine(line) {
    const maxLength = CHAT_BOARD_MAX_CHARACTERS_PER_LINE;
    const wrapped = [];
    let remaining = `${line || ""}`.replace(/\s+/g, " ").trim();

    while (remaining.length > maxLength) {
      let breakAt = remaining.lastIndexOf(" ", maxLength);
      if (breakAt < Math.floor(maxLength * 0.55)) breakAt = maxLength;

      wrapped.push(remaining.slice(0, breakAt).trimEnd());
      remaining =
        CHAT_BOARD_CONTINUATION_INDENT + remaining.slice(breakAt).trimStart();
    }

    if (remaining) wrapped.push(remaining);
    return wrapped;
  }

  _updateTutorialBoard() {
    if (!this.tutorialBoardText) return;
    const textComp = this.tutorialBoardText.getComponent("text");
    if (textComp) textComp.text = this.tutorialText;
  }

  _createChatUI() {
    if (document.getElementById("chat-ui-container")) return;
    const div = document.createElement("div");
    div.id = "chat-ui-container";
    div.style.cssText =
      "position: absolute; top: 10px; left: 10px; z-index: 1000; background: rgba(0,0,0,0.5); padding: 10px; border-radius: 8px;";
    div.innerHTML = `<input type="text" id="chatInput" placeholder="Senden..." style="width: 200px; padding: 5px;"><button id="sendBtn" style="padding: 5px 10px; cursor: pointer;">Send</button>`;
    document.body.appendChild(div);
    setTimeout(() => {
      const btn = document.getElementById("sendBtn");
      const input = document.getElementById("chatInput");
      if (!btn || !input) return;

      const submit = () => {
        if (this._submitChatText(input.value)) input.value = "";
      };
      btn.addEventListener("click", submit);
      input.addEventListener("keydown", (event) => {
        if (event.key === "Enter") submit();
      });
    }, 100);
  }

  _createLoginUI() {
    if (document.getElementById("communication-login-container")) return;

    const overlay = document.createElement("div");
    overlay.id = "communication-login-container";
    overlay.style.cssText = [
      "position:fixed",
      "inset:0",
      "z-index:3000",
      "display:flex",
      "align-items:center",
      "justify-content:center",
      "background:rgba(0,0,0,0.62)",
      "font-family:Arial,sans-serif",
    ].join(";");

    const panel = document.createElement("div");
    panel.style.cssText = [
      "width:min(360px,calc(100vw - 32px))",
      "background:#111827",
      "color:white",
      "border:1px solid rgba(255,255,255,0.18)",
      "border-radius:10px",
      "padding:18px",
      "box-shadow:0 18px 48px rgba(0,0,0,0.45)",
    ].join(";");

    panel.innerHTML = `
      <h2 style="margin:0 0 12px;font-size:20px;">Anmeldung</h2>
      <label style="display:block;margin-bottom:10px;font-size:13px;">
        Name
        <input id="communication-login-name" style="box-sizing:border-box;width:100%;margin-top:4px;padding:9px;border-radius:6px;border:1px solid #374151;" maxlength="32" />
      </label>
      <label style="display:block;margin-bottom:14px;font-size:13px;">
        Room ID
        <input id="communication-login-room" style="box-sizing:border-box;width:100%;margin-top:4px;padding:9px;border-radius:6px;border:1px solid #374151;" maxlength="48" />
      </label>
      <div style="margin-bottom:14px;font-size:13px;">
        Rolle
        <div style="display:flex;gap:8px;margin-top:6px;">
          <label style="flex:1;padding:8px;border:1px solid #374151;border-radius:6px;cursor:pointer;">
            <input type="radio" name="communication-login-role" value="host" />
            Host
          </label>
          <label style="flex:1;padding:8px;border:1px solid #374151;border-radius:6px;cursor:pointer;">
            <input type="radio" name="communication-login-role" value="guest" />
            Gast
          </label>
        </div>
      </div>
      <button id="communication-login-submit" style="width:100%;padding:10px;border:0;border-radius:6px;background:#16a34a;color:white;font-weight:700;cursor:pointer;">Enter</button>
      <p style="margin:10px 0 0;color:#cbd5e1;font-size:12px;line-height:1.35;">Danach bitte in der Szene Host oder Join auswaehlen.</p>
    `;

    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    const nameInput = document.getElementById("communication-login-name");
    const roomInput = document.getElementById("communication-login-room");
    const submit = document.getElementById("communication-login-submit");
    const roleInput = document.querySelector(
      `input[name="communication-login-role"][value="${this.preferredRole}"]`,
    );
    nameInput.value = this.myUsername || this.fallbackUsername;
    roomInput.value = this.serverId || "room-01";
    if (roleInput) roleInput.checked = true;

    const completeLogin = () => {
      const username = nameInput.value.trim() || this.fallbackUsername;
      const roomId = roomInput.value.trim() || this.serverId || "room-01";
      const selectedRole =
        document.querySelector('input[name="communication-login-role"]:checked')
          ?.value || "guest";
      this.myUsername = username;
      this.serverId = roomId;
      this.preferredRole = selectedRole;
      this._saveLoginSettings(username, roomId, selectedRole);
      this._appendLoginStatus();
      overlay.remove();
    };

    submit.addEventListener("click", completeLogin);
    overlay.addEventListener("keydown", (event) => {
      if (event.key === "Enter") completeLogin();
    });
    nameInput.focus();
  }

  // --- 后面是原本就有的同步逻辑 ---
  host() {
    if (!Peer) {
      setTimeout(() => this.host(), 500);
      return;
    }
    if (this.peer || this.isConnecting) {
      this._appendChatToBoard("System", "Already connecting or connected.");
      return;
    }
    this.isConnecting = true;
    this.peer = new Peer(this.serverId);
    this.peer.on("open", this._onHostOpen.bind(this));
    this.peer.on("connection", this._onHostConnected.bind(this));
    this.peer.on("disconnected", this._onDisconnected.bind(this));
    this.peer.on("error", (err) => {
      this.isConnecting = false;
      this._showPeerError(err, "host");
      console.error("Peer host error", err);
    });
    this.peer.on("call", (call) => this._handleIncomingMediaCall(call));
  }
  _onHostOpen(id) {
    this.isConnecting = false;
    this._setRole("host");
    this.serverId = id;
    this.activePlayers[this.serverId] = {};
    this._registerPlayerMetadata(this.serverId, this.myUsername, "host");
    this._appendChatToBoard("System", "Raum erstellt");
    this._startHostCameraPreview();
    for (const cb of this.connectionEstablishedCallbacks) cb();
  }

  _startHostCameraPreview() {
    if (this.localStream?.getVideoTracks().length) {
      if (!this.hostCameraPreview) this._createHostVideoBoard(this.localStream);
      return Promise.resolve(this.localStream);
    }
    if (this.hostMediaPromise) return this.hostMediaPromise;
    if (!navigator.mediaDevices?.getUserMedia) {
      this._appendChatToBoard("System", "Camera is not available.");
      return Promise.resolve(null);
    }

    this.hostMediaPromise = navigator.mediaDevices
      .getUserMedia({ audio: true, video: true })
      .then((stream) => {
        this._setLocalStream(stream);
        this._createHostVideoBoard(stream);
        for (const conn of this.connections) {
          if (conn.open) this.call(conn.peer);
        }
        this._appendChatToBoard("System", "Host camera preview started.");
        return stream;
      })
      .catch((err) => {
        this._appendChatToBoard(
          "System",
          "Camera permission was denied. Trying microphone only.",
        );
        console.error("Camera denied", err);
        return this._ensureGuestMicrophone();
      })
      .finally(() => {
        this.hostMediaPromise = null;
      });

    return this.hostMediaPromise;
  }
  _onHostConnected(c) {
    const username = c.metadata ? c.metadata.username : "Unknown";
    this._hostPlayerJoined(
      c.peer,
      username,
    );
    this.connections.push(c);
    c.on("open", () => {
      c.send({
        joinedPlayers: Object.keys(this.activePlayers),
        players: this._getPlayerDescriptors(),
        joined: true,
      });
      this._callWhenLocalMediaReady(c.peer);
      this._broadcastPeerList();
    });
    c.on("close", () => this._onHostConnectionClose(c));
    c.on("data", (d) => this._onHostDataRecieved(d, c));
  }
  _onHostDataRecieved(d, c) {
    if (d.type === "chat") {
      const chatMessage = this._appendChatMessage(d, "Guest");
      if (chatMessage) this._sendToGuests(chatMessage, c.peer);
      return;
    }
    if (this._handleCustomDataMessage(d, c.peer, true)) return;
    if (d.username) this._registerPlayerMetadata(c.peer, d.username, "guest");
    if (this._isPlayerTransformPacket(d)) {
      this._applyRemoteTransforms(c.peer, d.transforms, d);
      this.currentDataPacket[c.peer] = this._normalizePlayerTransformPacket(
        d,
        c.peer,
      );
    }
  }
  _onHostConnectionClose(c) {
    this._removePlayer(c.peer);
    this.connections = this.connections.filter((conn) => conn !== c);
    this._broadcastPeerList();
  }

  _broadcastPeerList() {
    if (this.role !== "host") return;
    const peers = this._getPlayerDescriptors();
    const data = { type: "peer-list", peers };
    for (const conn of this.connections) {
      if (conn.open) conn.send(data);
    }
  }
  _hostPlayerJoined(id, username = "Unknown") {
    if (!this.networkPlayerSpawner) return;
    this._registerPlayerMetadata(id, username, "guest");
    let n = this.networkPlayerSpawner.getEntity(username);
    if (n) {
      this.activePlayers[id] = n;
      this.currentDataPacket.joinedPlayers =
        this.currentDataPacket.joinedPlayers || [];
      this.currentDataPacket.joinedPlayers.push(id);
      this.currentDataPacket.players = this._getPlayerDescriptors();
    }
  }
  join() {
    this.connect(this.serverId);
  }
  connect(id) {
    if (!Peer) {
      setTimeout(() => this.connect(id), 500);
      return;
    }
    if (this.peer || this.isConnecting) {
      this._appendChatToBoard("System", "Already connecting or connected.");
      return;
    }
    this.isConnecting = true;
    if (!id) {
      this.isConnecting = false;
      return;
    }
    this.peer = new Peer();
    this.peer.on("open", this._clientOnOpen.bind(this));
    this.peer.on("disconnected", this._onDisconnected.bind(this));
    this.peer.on("error", (err) => {
      this.isConnecting = false;
      this._showPeerError(err, "guest");
      console.error("Peer connection error", err);
    });
    this.connectionId = id;
    this._ensureGuestMicrophone();
    this.peer.on("call", (call) => this._handleIncomingMediaCall(call));
  }
  _clientOnOpen() {
    this.connection = this.peer.connect(this.connectionId, {
      metadata: { username: this.myUsername },
    });
    this.connection.on("open", this._onClientConnected.bind(this));
    this.connection.on("data", this._onClientDataRecieved.bind(this));
    this.connection.on("close", this._onClientClose.bind(this));
    this.connection.on("error", (err) => {
      this._showPeerError(err, "guest");
      console.error("Data connection error", err);
    });
  }
  _onClientConnected() {
    this.isConnecting = false;
    this._setRole("guest");
    this._callWhenLocalMediaReady(this.serverId);
    this._appendChatToBoard("System", "Beigetreten als Gast");
  }
  _onClientDataRecieved(d) {
    if (d.type === "chat") {
      this._appendChatMessage(d, "Unknown");
      return;
    }
    if (d.type === "peer-list") {
      this._registerPlayerDescriptors(d.peers || []);
      this._spawnMissingPlayers(d.peers || []);
      this._connectVoicePeers(d.peers || []);
      return;
    }
    if (this._handleCustomDataMessage(d)) return;
    if (d.players) this._registerPlayerDescriptors(d.players);
    if (d.joinedPlayers) {
      this._spawnMissingPlayers(d.joinedPlayers);
      this._connectVoicePeers(d.joinedPlayers);
    }
    this._applyRemoteTransformPacket(d);
  }

  _applyRemoteTransformPacket(packet) {
    if (this._isPlayerTransformPacket(packet)) {
      const peerId = packet.senderId || packet.id || this.serverId;
      if (peerId !== this.peer?.id) {
        this._applyRemoteTransforms(peerId, packet.transforms, packet);
      }
      return;
    }

    for (const [peerId, data] of Object.entries(packet)) {
      if (
        this._isPacketMetaKey(peerId) ||
        peerId === this.peer?.id
      ) {
        continue;
      }
      if (data?.username) this._registerPlayerMetadata(peerId, data.username);
      if (this._isPlayerTransformPacket(data)) {
        this._applyRemoteTransforms(peerId, data.transforms, data);
      }
    }
  }

  _isPacketMetaKey(key) {
    return [
      "type",
      "joined",
      "joinedPlayers",
      "players",
      "username",
      "movement",
      "transforms",
      "senderId",
      "id",
      "roomId",
      "createdAt",
    ].includes(key);
  }

  _isPlayerTransformPacket(packet) {
    return Boolean(packet?.transforms);
  }

  _applyRemoteTransforms(peerId, transforms, data = {}) {
    const username =
      data.username || this.playerMetadata[peerId]?.username || peerId;
    if (!this.activePlayers[peerId] && this.networkPlayerSpawner) {
      const player = this.networkPlayerSpawner.getEntity(username);
      if (player) this.activePlayers[peerId] = player;
    }
    if (this.activePlayers[peerId]) {
      this.activePlayers[peerId].setName?.(username);
      this.activePlayers[peerId].setMovementState?.(data.movement);
      this.activePlayers[peerId].setTransforms(transforms);
    }
  }

  _connectVoicePeers(peers) {
    if (!this.voiceEnabled || !this.peer?.id) return;
    for (const id of this._normalizePeerIds(peers)) {
      if (!id || id === this.peer.id || this.calls[id]) continue;
      if (id === this.serverId || this.peer.id < id) {
        this._callWhenLocalMediaReady(id);
      }
    }
  }
  _addAudioStream(id, s) {
    if (
      this.role === "guest" &&
      id === this.serverId &&
      s.getVideoTracks().length > 0
    ) {
      this._showRemoteHostVideo(s);
    }

    if (document.getElementById("remoteAudio" + id)) return;
    const a = document.createElement("AUDIO");
    a.id = "remoteAudio" + id;
    document.body.appendChild(a);
    a.srcObject = s;
    a.autoplay = true;
    a.playsInline = true;
    this.streams[id] = s;
    this.remoteAudioElements[id] = a;
  }

  _showRemoteHostVideo(stream) {
    const remoteVideo = createStreamTextureOnObject(
      this.engine,
      this.hostVideoBoardObject || "videoboard",
      stream,
      { textureProperty: DEFAULT_TEXTURE_PROPERTY, muted: true },
    );
    if (remoteVideo) {
      this._setVideoBoard("remoteHostVideo", remoteVideo);
      this._appendChatToBoard("System", "Host camera stream connected.");
    }
  }

  toggleMute() {
    this.setMuted(!this.isMuted);
    return this.isMuted;
  }

  setMuted(muted) {
    this.isMuted = !!muted;
    this._applyMuteState();
    this._appendChatToBoard("System", this.isMuted ? "Microphone muted." : "Microphone unmuted.");
  }

  _applyMuteState() {
    if (!this.localStream) return;
    for (const track of this.localStream.getAudioTracks()) {
      track.enabled = !this.isMuted;
    }
  }

  getMuted() {
    return this.isMuted;
  }

  _removeAllPlayers() {
    for (const p of Object.keys(this.activePlayers)) this._removePlayer(p);
  }
  _removePlayer(id) {
    if (this.calls[id]) {
      const call = this.calls[id];
      delete this.calls[id];
      call.close();
    }
    if (this.streams[id]) {
      this.streams[id].getTracks().forEach((track) => track.stop());
      delete this.streams[id];
    }
    if (this.remoteAudioElements[id]) {
      this.remoteAudioElements[id].srcObject = null;
      this.remoteAudioElements[id].remove();
      delete this.remoteAudioElements[id];
    }
    if (this.activePlayers[id]) {
      if (this.networkPlayerSpawner?.returnEntity)
        this.networkPlayerSpawner.returnEntity(this.activePlayers[id]);
      delete this.activePlayers[id];
    }
  }
  _onDisconnected() {
    this._removeAllPlayers();
    this.disconnect();
  }
  _onClientClose() {
    if (this.isDisconnecting) return;
    this._appendChatToBoard("System", "Disconnected from host.");
    this._onDisconnected();
  }
  disconnect() {
    if (this.isDisconnecting) return;
    this.isDisconnecting = true;
    this._removeAllPlayers();
    for (const call of Object.values(this.calls)) call.close();
    this.calls = {};
    for (const audio of Object.values(this.remoteAudioElements)) {
      audio.srcObject = null;
      audio.remove();
    }
    this.remoteAudioElements = {};
    this.streams = {};
    this._setVideoBoard("hostCameraPreview", null);
    this._setVideoBoard("remoteHostVideo", null);
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = null;
    }
    for (const conn of this.connections) conn.close();
    this.connections = [];
    if (this.connection) {
      this.connection.close();
      this.connection = null;
    }
    if (this.peer) {
      this.peer.destroy();
      this.peer = null;
    }
    this._setRole("idle");
    this.isConnecting = false;
    this.isDisconnecting = false;
  }
  call(id) {
    if (!this.voiceEnabled || !this.localStream || !this.peer) return;
    if (this.calls[id]) return;
    const c = this.peer.call(id, this.localStream);
    if (!c) return;
    this.calls[id] = c;
    c.on("stream", (s) => this._addAudioStream(id, s));
    c.on("close", () => this._removePlayer(id));
    c.on("error", (err) => console.error("Media call error", err));
  }
  addConnectionEstablishedCallback(f) {
    this.connectionEstablishedCallbacks.push(f);
  }
  addClientJoinedCallback(f) {
    this.clientJoinedCallbacks.push(f);
  }
  addDisconnectCallback(f) {
    this.disconnectCallbacks.push(f);
  }
  addNetworkDataRecievedCallback(k, f) {
    this.registeredNetworkCallbacks[k] = f;
  }
  addNetworkDataReceivedCallback(k, f) {
    this.addNetworkDataRecievedCallback(k, f);
  }
  _registerPlayerMetadata(id, username, role = "guest") {
    if (!id) return;
    this.playerMetadata[id] = {
      ...(this.playerMetadata[id] || {}),
      id,
      username: username || this.playerMetadata[id]?.username || id,
      role,
    };
  }
  _registerPlayerDescriptors(players) {
    for (const player of players || []) {
      if (typeof player === "string") {
        this._registerPlayerMetadata(
          player,
          this.playerMetadata[player]?.username || player,
          this.playerMetadata[player]?.role || (player === this.serverId ? "host" : "guest"),
        );
      } else {
        this._registerPlayerMetadata(player.id, player.username, player.role);
      }
    }
  }
  _getPlayerDescriptors() {
    return Object.keys(this.activePlayers).map((id) => ({
      id,
      username: this.playerMetadata[id]?.username || id,
      role: this.playerMetadata[id]?.role || (id === this.serverId ? "host" : "guest"),
    }));
  }
  _normalizePeerIds(peers) {
    return (peers || []).map((peer) => (typeof peer === "string" ? peer : peer.id));
  }
  _spawnMissingPlayers(players) {
    for (const peerId of this._normalizePeerIds(players)) {
      if (!peerId || peerId === this.peer?.id || this.activePlayers[peerId]) continue;
      const username = this.playerMetadata[peerId]?.username || peerId;
      const n = this.networkPlayerSpawner?.getEntity(username);
      if (n) this.activePlayers[peerId] = n;
    }
  }
  _findObjectByName(name) {
    const result = this.engine.scene.findByName?.(name, true);
    if (Array.isArray(result)) return result[0] || null;
    return result || null;
  }
  _isInXRSession() {
    const xr = this.engine?.xr;
    if (!xr) return false;
    return Boolean(
      xr?.session ||
        xr?.currentSession ||
        xr?.xrSession ||
        this._readXRFlag(xr, "isPresenting") ||
        this._readXRFlag(xr, "isSessionActive"),
    );
  }
  _readXRFlag(xr, key) {
    const value = xr?.[key];
    if (typeof value !== "function") return value;
    try {
      return value.call(xr);
    } catch (_) {
      return false;
    }
  }
  _validPoseObject(object) {
    return object && typeof object.getTransformWorld === "function";
  }
  _getDesktopHeadObject() {
    if (this._validPoseObject(this.playerDesktopHead)) {
      return this.playerDesktopHead;
    }
    if (this._validPoseObject(this.fallbackDesktopHead)) {
      return this.fallbackDesktopHead;
    }
    this.fallbackDesktopHead = this._findObjectByName("NonVrCamera");
    return this._validPoseObject(this.fallbackDesktopHead)
      ? this.fallbackDesktopHead
      : null;
  }
  _getLocalHeadObject() {
    const desktopHead = this._getDesktopHeadObject();
    const xrHead = this._validPoseObject(this.playerHead)
      ? this.playerHead
      : null;
    if (this._isInXRSession()) return xrHead || desktopHead;
    return desktopHead || xrHead;
  }
  _buildMovementState(elapsedSeconds, headObject) {
    if (!headObject?.getPositionWorld || elapsedSeconds <= 0) {
      return this.localMovementState;
    }
    headObject.getPositionWorld(this.currentHeadPosition);
    if (!this.hasPreviousHeadPosition) {
      this.previousHeadPosition.set(this.currentHeadPosition);
      this.hasPreviousHeadPosition = true;
      return { state: "idle", moving: false, speed: 0 };
    }

    const dx = this.currentHeadPosition[0] - this.previousHeadPosition[0];
    const dy = this.currentHeadPosition[1] - this.previousHeadPosition[1];
    const dz = this.currentHeadPosition[2] - this.previousHeadPosition[2];
    const speed = Math.sqrt(dx * dx + dy * dy + dz * dz) / elapsedSeconds;
    this.previousHeadPosition.set(this.currentHeadPosition);
    const moving = speed > REMOTE_MOVING_SPEED_THRESHOLD;
    return {
      state: moving ? "moving" : "idle",
      moving,
      speed: Math.round(speed * 100) / 100,
    };
  }

  _createPlayerTransformPacket() {
    const senderId = this.peer?.id || this.serverId || "unknown";
    return {
      type: PLAYER_TRANSFORM_MESSAGE_TYPE,
      senderId,
      id: senderId,
      username: this.myUsername,
      role: this.role,
      roomId: this.serverId,
      createdAt: Date.now(),
      movement: this.localMovementState,
      transforms: {
        head: this.headDualQuat,
        rightHand: this.rightHandDualQuat,
        leftHand: this.leftHandDualQuat,
      },
    };
  }

  _normalizePlayerTransformPacket(packet, fallbackPeerId) {
    return {
      type: PLAYER_TRANSFORM_MESSAGE_TYPE,
      senderId: packet.senderId || packet.id || fallbackPeerId,
      id: packet.id || packet.senderId || fallbackPeerId,
      username: packet.username || this.playerMetadata[fallbackPeerId]?.username,
      role: packet.role || this.playerMetadata[fallbackPeerId]?.role || "guest",
      roomId: packet.roomId || this.serverId,
      createdAt: packet.createdAt || Date.now(),
      movement: packet.movement,
      transforms: packet.transforms,
    };
  }

  update(dt) {
    for (const board of this.videoBoards) board.update();
    if (!this.connection && this.connections.length == 0) return;
    this.currentTime += dt;
    if (this.currentTime < this.networkSendFrequencyInS) return;
    const sendElapsedSeconds = this.currentTime;
    this.currentTime = 0.0;
    const headObject = this._getLocalHeadObject();
    if (headObject) headObject.getTransformWorld(this.headDualQuat);
    if (this.playerRightHand)
      this.playerRightHand.getTransformWorld(this.rightHandDualQuat);
    if (this.playerLeftHand)
      this.playerLeftHand.getTransformWorld(this.leftHandDualQuat);
    this.localMovementState = this._buildMovementState(
      sendElapsedSeconds,
      headObject,
    );
    const localPacket = this._createPlayerTransformPacket();
    if (this.connections.length) {
      this.currentDataPacket[this.serverId] = localPacket;
      for (let c of this.connections) {
        const p = Object.fromEntries(
          Object.entries(this.currentDataPacket).filter((e) => e[0] != c.peer),
        );
        if (Object.keys(p).length) c.send(p);
      }
    } else {
      if (!this.connection?.open) return;
      this.currentDataPacket = localPacket;
      this.connection.send(localPacket);
    }
  }
}

export class PeerNetworkedPlayerPool extends Component {
  static TypeName = "peer-networked-player-pool";
  init() {
    this.inactivePool = [];
    for (let c of this.object.children) {
      const comp = c.getComponent("peer-networked-player");
      if (comp) {
        this.inactivePool.push(comp);
        c.active = false;
      }
    }
  }
  getEntity(username) {
    if (this.inactivePool.length) {
      const ent = this.inactivePool.shift();
      ent.object.active = true;
      if (ent.prepareForSpawn) {
        ent.prepareForSpawn(username || "");
      } else {
        ent.setName?.(username || "");
        ent.setMovementState?.({ state: "idle", moving: false, speed: 0 });
      }
      return ent;
    }
    return null;
  }
  returnEntity(e) {
    e.reset?.();
    e.object.active = false;
    this.inactivePool.push(e);
  }
}
export class PeerNetworkedPlayer extends Component {
  static TypeName = "peer-networked-player";
  static Properties = { nameTextObject: Property.object() };
  init() {
    this.displayName = "";
    this.movementState = { state: "idle", moving: false, speed: 0 };
    this.headPosition = new Float32Array(3);
    this.remoteTransforms = {
      head: this._createTransformState(),
      rightHand: this._createTransformState(),
      leftHand: this._createTransformState(),
    };
    this.nameTagCameraPosition = new Float32Array(3);
    this.nameTagPosition = new Float32Array(3);
    this.warnedMessages = {};
    this._syncChildReferences();
    this._prepareVisuals();
  }
  prepareForSpawn(username) {
    this._resetTransformStates();
    this._syncChildReferences();
    this._prepareVisuals();
    this.setName(username || "");
    this.setMovementState({ state: "idle", moving: false, speed: 0 });
  }
  update(dt) {
    const headUpdated = this._applySmoothedTransform(
      this.remoteTransforms.head,
      dt,
    );
    this._applySmoothedTransform(this.remoteTransforms.rightHand, dt);
    this._applySmoothedTransform(this.remoteTransforms.leftHand, dt);
    if (headUpdated) this._updateNameTagPosition();
  }
  _createTransformState() {
    return {
      current: new Float32Array(8),
      target: new Float32Array(8),
      hasCurrent: false,
      hasTarget: false,
      object: null,
    };
  }
  _resetTransformStates() {
    for (const state of Object.values(this.remoteTransforms || {})) {
      state.current.fill(0);
      state.target.fill(0);
      state.hasCurrent = false;
      state.hasTarget = false;
      state.object = null;
    }
  }
  _syncChildReferences() {
    this.head = null;
    this.leftHand = null;
    this.rightHand = null;
    const c = this._getChildren(this.object);
    for (let i = 0; i < c.length; i++) {
      if (c[i].name === "Head") this.head = c[i];
      else if (c[i].name === "LeftHand") this.leftHand = c[i];
      else if (c[i].name === "RightHand") this.rightHand = c[i];
      else if (this._isNameTagObject(c[i])) this.nameTextObject = c[i];
    }

    if (!this.head) this.head = this._findLikelyHeadChild(c);
    if (!this.head) this.head = this._createHeadObject();
  }
  _getChildren(object) {
    if (!object) return [];
    if (typeof object.getChildren === "function") {
      return object.getChildren([]);
    }
    return object.children || [];
  }
  _isNameTagObject(object) {
    return object?.name === "NameTag" || Boolean(object?.getComponent?.("text"));
  }
  _findLikelyHeadChild(children) {
    const candidates = children.filter((child) => !this._isNameTagObject(child));
    let best = null;
    let bestScore = -Infinity;
    for (const child of candidates) {
      const descendants = this._countDescendants(child, 3);
      const hasDirectMesh = Boolean(child.getComponent?.("mesh"));
      const hasShallowMesh = this._hasShallowMesh(child);
      const hasControllerShape = descendants > 4;
      let score = 0;
      if (hasDirectMesh) score += 4;
      if (hasShallowMesh) score += 3;
      if (child.childrenCount <= 1) score += 2;
      if (hasControllerShape) score -= 5;
      if (score > bestScore) {
        best = child;
        bestScore = score;
      }
    }
    return bestScore > 0 ? best : null;
  }
  _createHeadObject() {
    const head = this.engine.scene.addObject(this.object);
    head.name = "Head";
    head.setPositionLocal([0, 0, 0]);
    head.setRotationLocal([0, 0, 0, 1]);
    return head;
  }
  _hasShallowMesh(object) {
    if (object?.getComponent?.("mesh")) return true;
    for (const child of this._getChildren(object)) {
      if (child.getComponent?.("mesh")) return true;
    }
    return false;
  }
  _countDescendants(object, depth) {
    if (!object || depth <= 0) return 0;
    let count = 0;
    for (const child of this._getChildren(object)) {
      count += 1 + this._countDescendants(child, depth - 1);
    }
    return count;
  }
  _prepareVisuals() {
    this._ensureNameTag();
    this._ensureHeadMarker();
  }
  setTransforms(t) {
    if (!t) return;
    this._syncChildReferences();
    if (this.head && t.head) {
      if (this._queueTransform(this.head, t.head, this.remoteTransforms.head)) {
        this._updateNameTagPosition();
      } else {
        this._warnOnce("head-transform", "Invalid remote head transform.");
      }
    } else if (!this.head) {
      this._warnOnce(
        "missing-head",
        "Remote player prefab is missing a Head child.",
      );
    }
    if (this.rightHand && t.rightHand)
      this._queueTransform(
        this.rightHand,
        t.rightHand,
        this.remoteTransforms.rightHand,
      );
    if (this.leftHand && t.leftHand)
      this._queueTransform(
        this.leftHand,
        t.leftHand,
        this.remoteTransforms.leftHand,
      );
  }
  _queueTransform(object, transform, state) {
    if (
      !object ||
      !state ||
      !this._copyNetworkTransform(transform, state.target)
    ) {
      return false;
    }

    state.object = object;
    state.hasTarget = true;
    if (!state.hasCurrent) {
      state.current.set(state.target);
      state.hasCurrent = true;
      object.setTransformLocal(state.current);
    }
    return true;
  }
  _applySmoothedTransform(state, dt) {
    if (!state?.object || !state.hasCurrent || !state.hasTarget) return false;
    const alpha =
      dt > 0 ? 1 - Math.exp(-REMOTE_TRANSFORM_SMOOTHING * dt) : 1;
    this._lerpDualQuat(state.current, state.target, Math.min(1, alpha));
    state.object.setTransformLocal(state.current);
    return true;
  }
  _lerpDualQuat(current, target, alpha) {
    const dot =
      current[0] * target[0] +
      current[1] * target[1] +
      current[2] * target[2] +
      current[3] * target[3];
    const sign = dot < 0 ? -1 : 1;
    for (let i = 0; i < 8; i++) {
      const targetValue = target[i] * sign;
      current[i] += (targetValue - current[i]) * alpha;
    }
    this._normalizeDualQuat(current);
  }
  _normalizeDualQuat(transform) {
    const len = Math.hypot(
      transform[0],
      transform[1],
      transform[2],
      transform[3],
    );
    if (!Number.isFinite(len) || len <= 0.000001) return;
    for (let i = 0; i < 8; i++) transform[i] /= len;
  }
  _copyNetworkTransform(transform, target) {
    if (!transform || !target) return false;
    let source = transform;
    if (source instanceof ArrayBuffer) source = new Float32Array(source);
    if (
      ArrayBuffer.isView(source) &&
      typeof source.length === "number" &&
      source.length >= 8
    ) {
      target.set(source.subarray ? source.subarray(0, 8) : source.slice(0, 8));
      return true;
    }
    if (typeof source.length !== "number" && typeof source !== "object") {
      return false;
    }
    for (let i = 0; i < 8; i++) {
      const value = Number(source[i]);
      if (!Number.isFinite(value)) return false;
      target[i] = value;
    }
    return true;
  }
  _warnOnce(key, message) {
    if (this.warnedMessages[key]) return;
    this.warnedMessages[key] = true;
    console.warn(`[PeerNetworkedPlayer] ${message}`, this.object?.name || "");
  }
  setName(name) {
    this.displayName = name || "";
    this._refreshNameTag();
  }
  setMovementState(movement) {
    if (movement) this.movementState = movement;
    this._refreshNameTag();
  }
  reset() {
    this._resetTransformStates();
    this.object.setPositionLocal([0, 0, 0]);
    this.object.setRotationLocal([0, 0, 0, 1]);
    this.setName("");
    this.setMovementState({ state: "idle", moving: false, speed: 0 });
  }
  _ensureNameTag() {
    if (!this.nameTextObject) {
      this.nameTextObject = this.engine.scene.addObject(this.object);
      this.nameTextObject.name = "NameTag";
      this.nameTextObject.setPositionLocal([0, REMOTE_NAME_TAG_OFFSET_Y, 0]);
    }
    this.nameTextObject.setScalingLocal([
      REMOTE_NAME_TAG_SCALE,
      REMOTE_NAME_TAG_SCALE,
      REMOTE_NAME_TAG_SCALE,
    ]);
    this.nameText = this.nameTextObject.getComponent("text");
    if (!this.nameText) this.nameText = this.nameTextObject.addComponent("text");
    this._applyTextMaterial();
    this.nameText.alignment = 1;
    this.nameText.verticalAlignment = 1;
    this.nameText.justification = 1;
    this._refreshNameTag();
  }
  _applyTextMaterial() {
    if (!this.nameText || this.nameText.material) return;
    const material = this._findReusableTextMaterial();
    if (material) this.nameText.material = material;
  }
  _findReusableTextMaterial() {
    const roots = this.engine.scene.children || [];
    for (const root of roots) {
      const material = this._findTextMaterialInObject(root, 0);
      if (material) return material;
    }
    return null;
  }
  _findTextMaterialInObject(object, depth) {
    if (!object || depth > 8) return null;
    const text = object.getComponent?.("text");
    if (text?.material && object !== this.nameTextObject) return text.material;
    for (const child of this._getChildren(object)) {
      const material = this._findTextMaterialInObject(child, depth + 1);
      if (material) return material;
    }
    return null;
  }
  _ensureHeadMarker() {
    if (!this.head) return;
    const existingMarker = this._findMeshHolder(this.head);
    if (existingMarker) {
      this.headMarker = existingMarker;
      this._setHeadMarkerScale();
      return;
    }
    const source = this._findMarkerMeshSource();
    if (!source?.mesh) return;

    this.headMarker = this.engine.scene.addObject(this.head);
    this.headMarker.name = "HeadMarker";
    this.headMarker.setPositionLocal([0, 0, 0]);
    this._setHeadMarkerScale();

    const mesh = this.headMarker.addComponent("mesh");
    mesh.mesh = source.mesh;
    if (source.material) mesh.material = source.material;
  }
  _setHeadMarkerScale() {
    if (!this.headMarker?.setScalingLocal) return;
    this.headMarker.setScalingLocal([
      REMOTE_HEAD_MARKER_SCALE,
      REMOTE_HEAD_MARKER_SCALE,
      REMOTE_HEAD_MARKER_SCALE,
    ]);
  }
  _findMeshHolder(object) {
    if (object?.getComponent?.("mesh")) return object;
    for (const child of this._getChildren(object)) {
      const found = this._findMeshHolder(child);
      if (found) return found;
    }
    return null;
  }
  _hasMesh(object) {
    if (object.getComponent?.("mesh")) return true;
    for (const child of this._getChildren(object)) {
      if (this._hasMesh(child)) return true;
    }
    return false;
  }
  _findMarkerMeshSource() {
    for (const name of ["3DCursorHitRight", "3DCursorHitLeft"]) {
      const obj = this._findObjectByName(name);
      const mesh = obj?.getComponent?.("mesh");
      if (mesh?.mesh) return mesh;
    }
    return this._findFirstMesh(this.engine.scene, 0);
  }
  _findObjectByName(name) {
    const result = this.engine.scene.findByName?.(name, true);
    if (Array.isArray(result)) return result[0] || null;
    return result || null;
  }
  _findFirstMesh(object, depth) {
    if (!object || depth > 6) return null;
    const mesh = object.getComponent?.("mesh");
    if (mesh?.mesh) return mesh;
    for (const child of this._getChildren(object)) {
      const found = this._findFirstMesh(child, depth + 1);
      if (found) return found;
    }
    return null;
  }
  _updateNameTagPosition() {
    if (!this.nameTextObject || !this.head?.getPositionWorld) return;
    this.head.getPositionWorld(this.headPosition);
    this.nameTextObject.setPositionWorld([
      this.headPosition[0],
      this.headPosition[1] + REMOTE_NAME_TAG_OFFSET_Y,
      this.headPosition[2],
    ]);
    this._faceNameTagToCamera();
  }
  _faceNameTagToCamera() {
    if (!this.nameTextObject?.lookAt) return;
    const camera =
      this._findObjectByName("NonVrCamera") || this._findObjectByName("Head");
    if (!camera?.getPositionWorld) return;
    this.nameTextObject.getPositionWorld(this.nameTagPosition);
    camera.getPositionWorld(this.nameTagCameraPosition);
    this.nameTextObject.lookAt(this.nameTagCameraPosition);
    this.nameTextObject.rotateAxisAngleDegLocal([0, 1, 0], 180);
    this.nameTextObject.setPositionWorld(this.nameTagPosition);
  }
  _refreshNameTag() {
    if (!this.nameText) return;
    const state = this.movementState?.state || "idle";
    this.nameText.text = this.displayName ? `${this.displayName}\n${state}` : "";
  }
}
export class PeerNetworkedPlayerSpawner extends Component {
  static TypeName = "peer-networked-player-spawner";
  static Properties = {
    headMesh: Property.mesh(),
    headMaterial: Property.material(),
  };
}
